import type {Command, EditorView, KeyBinding} from '@codemirror/view'
import {ActionBar, type IconButtonProps} from '@primer/react'
import type {Nodes} from 'mdast'

import type {SelectionContextValue} from '../codemirror/extensions/selection-context'
import {useSelectionContext} from '../context/SelectionContext'

interface ToolbarButtonProps
  extends Omit<
    IconButtonProps,
    'onClick' | 'keybindingHint' | 'onMouseDown' | 'variant' | 'aria-labelledby' | 'aria-pressed'
  > {
  view: EditorView | null
  command: Command | KeyBinding
  matchingNodeFilter: (node: Nodes) => boolean
  ['aria-label']: string
  keybindingHint?: string
  resolveIsPressed?: (selectionContext: SelectionContextValue, defaultPressed: boolean) => boolean
}

function formatKeybindingHint(input?: string) {
  return input?.replaceAll('+', 'Plus')?.replaceAll('-', '+')
}

export const ToolbarButton = ({
  command,
  view,
  matchingNodeFilter,
  keybindingHint,
  resolveIsPressed,
  ...props
}: ToolbarButtonProps) => {
  const selectionContext = useSelectionContext()
  const defaultIsPressed = selectionContext.some(matchingNodeFilter)
  const isPressed = resolveIsPressed ? resolveIsPressed(selectionContext, defaultIsPressed) : defaultIsPressed

  const commandIsKeybinding = typeof command !== 'function'
  const fallbackCommand: Command = () => false
  const commandFn = (commandIsKeybinding ? command.run : command) ?? fallbackCommand
  const commandHint = keybindingHint ?? (commandIsKeybinding ? command.key : undefined)

  const runCommand = () => {
    if (!view) return

    // Toolbar clicks can steal focus, so ensure the editor is focused before invoking CodeMirror commands.
    if (!view.hasFocus) view.focus()

    const runWithEventSource = commandFn as (editorView: EditorView, eventSource?: 'keyboard' | 'toolbar') => boolean
    runWithEventSource(view, 'toolbar')
  }

  return (
    <ActionBar.IconButton
      variant="invisible"
      // Prevent focus leaving input:
      onMouseDown={e => e.preventDefault()}
      keybindingHint={formatKeybindingHint(commandHint)}
      disabled={!view}
      onClick={runCommand}
      aria-pressed={isPressed}
      {...props}
    />
  )
}

ToolbarButton.displayName = 'MarkdownEditor.ToolbarButton'
